import { useState } from 'react'
import { Card } from '../components/ui/Card'
import { Avatar } from '../components/ui/Avatar'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { KudosModal } from '../components/safety/KudosModal'
import { KidReportModal } from '../components/safety/KidReportModal'
import { usePerson } from '../contexts/PersonContext'
import { useFriends } from '../hooks/useFriends'
import type { FriendDto } from '../types/community'

type ModalState = { kind: 'kudos' | 'report'; friend: FriendDto } | null

/** My friends (COTC_FriendsApprovals R5 / DESIGN §3.11), kid role. */
export function MyFriendsPage() {
  const { activePerson, guardianFirst } = usePerson()
  const actingId = activePerson?.id
  const friends = useFriends(actingId)
  const [modal, setModal] = useState<ModalState>(null)
  const rows = friends.data ?? []

  return (
    <div className="animate-rise px-4 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">My friends</div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">Your friends</h1>
      <p className="mt-3 max-w-[60ch] text-[15px] leading-relaxed text-ink-muted">
        If someone is mean, unsafe, or asks you for anything private — tell us. You will never get in trouble for reporting.
      </p>

      {friends.isLoading ? (
        <p className="mt-8 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
      ) : friends.isError ? (<div role="alert" className="mt-6 text-[13px] text-honey-fg">Could not load your friends. <Button variant="outline" size="sm" onClick={() => void friends.refetch()}>Retry friends</Button></div>) : rows.length === 0 ? (
        <EmptyState
          className="mt-8"
          dashed
          description={`No friends in this circle yet. Ask ${guardianFirst || 'a grown-up'} to invite someone, or look at another circle.`}
        />
      ) : (
        <div className="mt-6 space-y-3">
          {rows.map((f) => (
            <Card key={f.personId} className="flex min-w-0 flex-wrap items-center gap-3 sm:gap-4">
              <Avatar initials={(f.firstName || '?')[0].toUpperCase()} tintIndex={f.tintIndex} size={40} showDot online={f.online} ringClass="border-paper-surface" />
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-ink">{f.firstName}</div>
                <div className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                  {f.metaText || (f.online ? 'online now' : 'offline')}
                </div>
              </div>
              <div className="flex flex-none items-center gap-2">
                <Button variant="outline" size="sm" aria-label={`Give ${f.firstName} kudos`} onClick={() => setModal({ kind: 'kudos', friend: f })}>
                  ♡ {f.kudosCount}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setModal({ kind: 'report', friend: f })}>Report</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal?.kind === 'kudos' && (
        <KudosModal
          open
          onClose={() => setModal(null)}
          targetName={modal.friend.firstName}
          actingPersonId={actingId}
          targetPersonId={modal.friend.personId}
        />
      )}
      {modal?.kind === 'report' && (
        <KidReportModal
          open
          onClose={() => setModal(null)}
          targetName={modal.friend.firstName}
          guardianFirst={guardianFirst}
          actingPersonId={actingId}
          targetPersonId={modal.friend.personId}
        />
      )}
    </div>
  )
}
